import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Fuel as FuelIcon, Plus, Trash2, AlertTriangle, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import DateRangeBar from '../components/DateRangeBar';

const R = (v) => `R${parseFloat(v || 0).toFixed(2)}`;
// entry_date comes back as an ISO timestamp; show it as a plain date
const day = (d) => (d ? new Date(d).toLocaleDateString('en-ZA') : '—');

function AddFuel({ onAdded }) {
  const blank = { entry_date: new Date().toISOString().split('T')[0], vehicle: '', liters: '', total_cost: '' };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    if (!f.total_cost) return toast.error('Enter what the fuel cost');
    setBusy(true);
    try {
      await api.post('/billing/fuel', {
        ...f, liters: f.liters || null, total_cost: parseFloat(f.total_cost),
      });
      toast.success('Fuel entry saved');
      setF(blank);
      onAdded();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <h3 className="font-semibold text-gray-700 text-sm mb-3">Add a fill-up</h3>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
          <input type="date" value={f.entry_date} onChange={set('entry_date')}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Vehicle</label>
          <input type="text" value={f.vehicle} onChange={set('vehicle')} placeholder="e.g. CEM37298"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Litres</label>
          <input type="number" step="0.01" value={f.liters} onChange={set('liters')}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Cost (R) *</label>
          <input type="number" step="0.01" value={f.total_cost} onChange={set('total_cost')}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        <button onClick={save} disabled={busy}
          className="flex items-center justify-center gap-1 bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50">
          <Plus size={15} /> {busy ? 'Saving…' : 'Add'}
        </button>
      </div>
    </div>
  );
}

export default function Fuel() {
  const qc = useQueryClient();
  const today = new Date().toISOString().split('T')[0];
  const [from, setFrom] = useState(new Date(Date.now() - 90 * 86400000).toISOString().split('T')[0]);
  const [to, setTo] = useState(today);
  const [branch, setBranch] = useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['fuel', from, to, branch],
    queryFn: () => api.get('/billing/fuel', { params: { from, to, branch } }).then((r) => r.data),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['fuel'] });

  const remove = async (row) => {
    if (!window.confirm(`Delete the ${day(row.entry_date)} fill of ${R(row.total_cost)}?`)) return;
    try { await api.delete(`/billing/fuel/${row.id}`); toast.success('Deleted'); refresh(); }
    catch { toast.error('Could not delete'); }
  };

  // Accepting a flagged entry brings it back into the totals.
  const accept = async (row) => {
    try {
      await api.patch(`/billing/fuel/${row.id}`, { resolve_review: true });
      toast.success('Accepted — now counted in expenses'); refresh();
    } catch { toast.error('Could not update'); }
  };

  const t = data?.totals;
  const rows = data?.rows || [];
  const flagged = rows.filter((r) => r.needs_review);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <FuelIcon size={22} className="text-orange-500" />
        <div>
          <h1 className="text-xl font-bold text-gray-800">Fuel</h1>
          <p className="text-xs text-gray-400">Petrol and diesel spend, for the books</p>
        </div>
      </div>

      <DateRangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} />

      {/* The Fastway franchise earns its own income, which this system never
          sees — so its fuel is kept out of the courier profit figure. */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500 mr-1">Branch:</span>
        {[['all', 'All'], ['expressway', 'Expressway'], ['fastway', 'Fastway franchise']].map(([k, lbl]) => (
          <button key={k} onClick={() => setBranch(k)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              branch === k ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-300 text-gray-600 hover:border-orange-400'
            }`}>
            {lbl}
            {data?.by_branch && k !== 'all' && (
              <span className="opacity-60 ml-1">
                {R(data.by_branch.find((b) => b.branch === k)?.total || 0)}
              </span>
            )}
          </button>
        ))}
        <span className="text-xs text-gray-400 ml-auto">
          Only <strong>Expressway</strong> fuel counts towards Income vs Expenses
        </span>
      </div>

      <AddFuel onAdded={refresh} />

      {isLoading ? <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">Loading…</div> : t && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl p-4 bg-red-50 text-red-700">
              <p className="text-xl font-bold">{R(t.total)}</p>
              <p className="text-xs mt-0.5 opacity-75">Fuel spend</p>
            </div>
            <div className="rounded-xl p-4 bg-gray-50 text-gray-700">
              <p className="text-xl font-bold">{parseFloat(t.liters).toFixed(0)} L</p>
              <p className="text-xs mt-0.5 opacity-75">Litres</p>
            </div>
            <div className="rounded-xl p-4 bg-blue-50 text-blue-700">
              <p className="text-xl font-bold">{R(t.avg_per_liter)}</p>
              <p className="text-xs mt-0.5 opacity-75">Average per litre</p>
            </div>
            <div className={`rounded-xl p-4 ${t.flagged > 0 ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>
              <p className="text-xl font-bold">{t.flagged}</p>
              <p className="text-xs mt-0.5 opacity-75">Need checking</p>
              {t.flagged > 0 && <p className="text-[11px] opacity-70">{R(t.flagged_total)} not counted</p>}
            </div>
          </div>

          {flagged.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                <AlertTriangle size={15} /> {flagged.length} entries look wrong and are NOT counted in your expenses
              </p>
              <p className="text-xs text-amber-700 mt-1">
                The price per litre is far outside the normal range — usually a misread slip. Fix the numbers
                on the Fastway tracker and re-sync, or accept it here if the amount really is correct.
              </p>
              <div className="mt-3 space-y-1">
                {flagged.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 text-xs bg-white rounded-lg px-3 py-2">
                    <span className="text-gray-500">{day(r.entry_date)}</span>
                    <span className="text-gray-700">{r.vehicle}</span>
                    <span className="font-medium">{r.liters}L · {R(r.total_cost)}</span>
                    <span className="text-amber-700 flex-1">{r.review_reason}</span>
                    <button onClick={() => accept(r)} title="The amount is correct — count it"
                      className="flex items-center gap-1 border border-gray-300 rounded px-2 py-1 hover:bg-gray-50">
                      <Check size={12} /> Accept
                    </button>
                    <button onClick={() => remove(r)} className="text-gray-400 hover:text-red-500"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <h3 className="font-semibold text-gray-700 px-5 pt-4 pb-2 text-sm">Month by month</h3>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="px-4 py-2 text-left">Month</th>
                  <th className="px-4 py-2 text-right">Fills</th>
                  <th className="px-4 py-2 text-right">Litres</th>
                  <th className="px-4 py-2 text-right">Spend</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.monthly.map((m) => (
                  <tr key={m.month} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium text-gray-700">
                      {new Date(`${m.month}-01T00:00:00`).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-500">{m.fills}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{parseFloat(m.liters).toFixed(1)}</td>
                    <td className="px-4 py-2 text-right font-semibold text-gray-800">{R(m.total)}</td>
                  </tr>
                ))}
                {data.monthly.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No fuel recorded in this period</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <h3 className="font-semibold text-gray-700 px-5 pt-4 pb-2 text-sm">Every fill-up</h3>
            <div className="overflow-x-auto max-h-[32rem] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left">Date</th>
                    <th className="px-4 py-2 text-left">Vehicle</th>
                    <th className="px-4 py-2 text-right">Litres</th>
                    <th className="px-4 py-2 text-right">Cost</th>
                    <th className="px-4 py-2 text-right">Per litre</th>
                    <th className="px-4 py-2 text-left">Source</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((r) => {
                    const ppl = r.liters > 0 ? r.total_cost / r.liters : null;
                    return (
                      <tr key={r.id} className={`hover:bg-gray-50 ${r.needs_review ? 'bg-amber-50' : ''}`}>
                        <td className="px-4 py-2 text-gray-500 text-xs whitespace-nowrap">{day(r.entry_date)}</td>
                        <td className="px-4 py-2 text-gray-700 text-xs">{r.vehicle || '—'}</td>
                        <td className="px-4 py-2 text-right text-gray-600 text-xs">{r.liters ? parseFloat(r.liters).toFixed(2) : '—'}</td>
                        <td className="px-4 py-2 text-right font-medium text-gray-800 text-xs">{R(r.total_cost)}</td>
                        <td className={`px-4 py-2 text-right text-xs ${r.needs_review ? 'text-amber-700 font-semibold' : 'text-gray-400'}`}>
                          {ppl ? R(ppl) : '—'}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {r.source === 'fastway'
                            ? <span className="bg-gray-100 text-gray-500 rounded px-1.5 py-0.5 text-[10px]">Fastway</span>
                            : <span className="text-gray-400">manual</span>}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button onClick={() => remove(r)} className="text-gray-300 hover:text-red-500"><Trash2 size={13} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
